import { createRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconCheck } from '@tabler/icons-react';
import {
  COURSE_CATEGORIES,
  DEFAULT_CATEGORY_LABELS,
  SUB_CATEGORIES,
  subCategoryOverrideKey,
} from '@admitly/shared';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { ApiError } from '../../../../lib/api.js';
import { useCategoryLabels, useUpdateCategoryLabels } from '../../../../hooks/useCategoryLabels.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { Label } from '../../../../components/ui/Label.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/categories',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'admin') throw redirect({ to: '/app' });
  },
  component: CategoriesPage,
});

function CategoriesPage() {
  const labels = useCategoryLabels();
  const update = useUpdateCategoryLabels();

  // Form state keyed by category / `${category}.${slug}`. Empty = use default.
  const [catLabels, setCatLabels] = useState<Record<string, string>>({});
  const [subLabels, setSubLabels] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the form once the current overrides load.
  useEffect(() => {
    if (labels.data) {
      setCatLabels({ ...(labels.data.category_labels as Record<string, string>) });
      setSubLabels({ ...labels.data.sub_category_labels });
    }
  }, [labels.data]);

  async function handleSave() {
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({
        category_labels: catLabels,
        sub_category_labels: subLabels,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Course categories"
        description="Rename the four categories and their sub-categories for your team. Leave a field blank to use the system default."
      />

      {labels.isLoading && <p className="text-muted">Loading…</p>}
      {labels.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load categories: {(labels.error as Error).message}
        </p>
      )}

      {labels.data && (
        <div className="max-w-2xl space-y-8">
          {saved && (
            <div
              className="rounded-lg px-4 py-3 text-sm flex items-center gap-2"
              style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
            >
              <IconCheck size={16} /> <strong>Saved.</strong> Your team sees the new names.
            </div>
          )}
          {error && (
            <div
              className="rounded-md px-3 py-2 text-[13px]"
              style={{ backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }}
            >
              {error}
            </div>
          )}

          {COURSE_CATEGORIES.map((cat) => (
            <section key={cat} className="space-y-3">
              <div>
                <Label htmlFor={`cat-${cat}`}>Category</Label>
                <Input
                  id={`cat-${cat}`}
                  value={catLabels[cat] ?? ''}
                  onChange={(e) => setCatLabels((s) => ({ ...s, [cat]: e.target.value }))}
                  placeholder={DEFAULT_CATEGORY_LABELS[cat]}
                  maxLength={60}
                  className="max-w-md"
                />
              </div>

              <div className="ml-4 border-l border-line pl-4 space-y-2">
                <div className="text-xs uppercase tracking-wider text-muted">Sub-categories</div>
                {SUB_CATEGORIES[cat].map((sub) => {
                  const key = subCategoryOverrideKey(cat, sub.slug);
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <span className="w-56 shrink-0 text-sm text-muted">{sub.label}</span>
                      <Input
                        value={subLabels[key] ?? ''}
                        onChange={(e) => setSubLabels((s) => ({ ...s, [key]: e.target.value }))}
                        placeholder={sub.label}
                        maxLength={60}
                        aria-label={`Rename ${sub.label}`}
                        className="max-w-xs"
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="flex items-center gap-3">
            <Button onClick={handleSave} loading={update.isPending}>
              Save categories
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
